import { useState, useEffect, useCallback } from "react";
import {
  DollarSign, TrendingUp, Building2, Loader2, CreditCard, Package,
  Plus, BarChart3, ArrowUpRight, ArrowDownRight, Receipt,
  Search, RefreshCw, AlertTriangle, CheckCircle2, Clock,
  Sparkles, Settings2, Users, ArrowUp, ArrowDown, Wallet,
  PieChart, Activity, ChevronRight, Trash2, Calendar, Eye, Shield,
} from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart as RPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

/* ─── constants ─── */
const TABS = [
  { id: "revenue", label: "Revenue Overview", icon: DollarSign },
  { id: "invoices", label: "Invoices", icon: Receipt },
  { id: "forecasts", label: "Forecasts", icon: TrendingUp },
  { id: "subscription", label: "Subscription Plans", icon: Package },
];

const PERIODS = [
  { id: "this_month", label: "This Month" },
  { id: "this_quarter", label: "This Quarter" },
  { id: "this_year", label: "This Year" },
  { id: "last_quarter", label: "Last Quarter" },
  { id: "last_12_months", label: "Last 12 Months" },
];

const CHART_COLORS = ["#22c55e", "#3b82f6", "#a855f7", "#f59e0b", "#ec4899", "#06b6d4"];
const fmt = (n) => {
  if (n == null) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const KPICard = ({ label, value, sub, icon: Icon, color, trend, trendDir }) => (
  <Card className="border shadow-none" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", color)}>
          <Icon className="w-4 h-4" />
        </div>
        {trend && (
          <span className={cn("text-[10px] font-medium flex items-center gap-0.5", trendDir === "up" ? "text-green-600" : "text-red-500")}>
            {trendDir === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trend}
          </span>
        )}
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {sub && <p className="text-[9px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

/* ═══════════════════════════════════
   REVENUE OVERVIEW TAB
   ═══════════════════════════════════ */
const RevenueTab = ({ data, loading, period, setPeriod }) => {
  const nav = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const [tenantSearch, setTenantSearch] = useState("");

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-16" />;
  if (!data) return null;
  const { kpis, tenant_revenue, mrr_trend, revenue_by_source, revenue_by_tier } = data;

  const top10 = tenant_revenue?.slice(0, 10) || [];
  const allFiltered = (tenant_revenue || []).filter(t =>
    !tenantSearch || t.tenant_name?.toLowerCase().includes(tenantSearch.toLowerCase())
  );
  const displayTenants = showAll ? allFiltered : top10;

  return (
    <div className="space-y-5">
      {/* Period Selector */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
        {PERIODS.map(p => (
          <Button key={p.id} size="sm" variant={period === p.id ? "default" : "outline"}
            className="h-7 text-[11px]" onClick={() => setPeriod(p.id)} data-testid={`period-${p.id}`}>
            {p.label}
          </Button>
        ))}
        {data.period_label && <span className="text-[10px] text-muted-foreground ml-2">{data.period_label}</span>}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard label="Monthly Recurring Revenue" value={fmt(kpis.total_mrr)} icon={DollarSign} color="text-green-600 bg-green-50" trend={`+${kpis.growth_rate}%`} trendDir="up" sub="MRR" />
        <KPICard label="Annual Recurring Revenue" value={fmt(kpis.total_arr)} icon={TrendingUp} color="text-blue-600 bg-blue-50" sub="ARR" />
        <KPICard label="Period Revenue" value={fmt(kpis.period_revenue)} icon={Wallet} color="text-violet-600 bg-violet-50" />
        <KPICard label="Avg Revenue / Tenant" value={fmt(kpis.avg_revenue_per_tenant)} icon={Building2} color="text-amber-600 bg-amber-50" sub={`${kpis.total_tenants} tenants`} />
        <KPICard label="Net Revenue Retention" value={`${kpis.net_revenue_retention}%`} icon={Activity} color="text-teal-600 bg-teal-50" trend="+2.1%" trendDir="up" />
        <KPICard label="Est. Customer LTV" value={fmt(kpis.ltv_estimate)} icon={CreditCard} color="text-rose-600 bg-rose-50" sub="36-month" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MRR Trend Line Chart */}
        <Card className="border shadow-none lg:col-span-2" data-testid="mrr-trend-chart">
          <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">MRR Trend</CardTitle></CardHeader>
          <CardContent className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={mrr_trend}>
                <defs>
                  <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={v => v?.split(" ")[0]} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmt(v)} width={55} />
                <Tooltip formatter={(v) => [fmt(v), "MRR"]} labelStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="mrr" stroke="#22c55e" fill="url(#mrrGrad)" strokeWidth={2} dot={{ r: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue by Source - Donut */}
        <Card className="border shadow-none" data-testid="revenue-source-chart">
          <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Revenue by Source</CardTitle></CardHeader>
          <CardContent className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={220}>
              <RPieChart>
                <Pie data={revenue_by_source} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {revenue_by_source?.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              </RPieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Tier Bar */}
      {revenue_by_tier?.length > 0 && (
        <Card className="border shadow-none" data-testid="revenue-tier-chart">
          <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Revenue by Plan Tier</CardTitle></CardHeader>
          <CardContent className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revenue_by_tier} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmt(v)} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={90} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Top Revenue Tenants */}
      <Card className="border shadow-none" data-testid="revenue-by-tenant">
        <CardHeader className="pt-4 pb-2 px-5">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">{showAll ? "All Tenants" : "Top 10 Revenue Clients"}</CardTitle>
            <div className="flex items-center gap-2">
              {showAll && (
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  <Input value={tenantSearch} onChange={e => setTenantSearch(e.target.value)}
                    placeholder="Search tenants..." className="pl-7 h-7 text-xs w-48" data-testid="tenant-search" />
                </div>
              )}
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { setShowAll(!showAll); setTenantSearch(""); }} data-testid="toggle-all-tenants">
                {showAll ? "Top 10" : "View All"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b bg-muted/30">
                {["#", "Tenant", "Plan", "Users", "Billable", "Rate", "MRR", "ARR", "ILT Rev", "Total", ""].map(h => (
                  <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {displayTenants.map((t, i) => (
                  <tr key={t.tenant_id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => nav(`/admin/tenant-revenue/${t.tenant_id}`)}>
                    <td className="p-2 text-xs text-muted-foreground">{showAll ? i + 1 : i + 1}</td>
                    <td className="p-2">
                      <p className="text-sm font-medium">{t.tenant_name}</p>
                      <p className="text-[9px] text-muted-foreground">{t.status}{t.trial_active ? " (trial)" : ""}</p>
                    </td>
                    <td className="p-2"><Badge variant="outline" className="text-[9px] capitalize">{t.plan_id}</Badge></td>
                    <td className="p-2 text-sm text-center">{t.active_users}</td>
                    <td className="p-2 text-sm text-center">{t.billable_users}</td>
                    <td className="p-2 text-xs">${t.rate}</td>
                    <td className="p-2 text-sm font-bold">{fmt(t.mrr)}</td>
                    <td className="p-2 text-sm text-muted-foreground">{fmt(t.arr)}</td>
                    <td className="p-2 text-xs">{fmt(t.ilt_revenue)}</td>
                    <td className="p-2 text-sm font-bold text-green-700">{fmt(t.total_revenue)}</td>
                    <td className="p-2"><ChevronRight className="w-3 h-3 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════
   INVOICES TAB
   ═══════════════════════════════════ */
const InvoicesTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("due_date");
  const [sortDir, setSortDir] = useState("desc");

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ sort_by: sortBy, sort_dir: sortDir, limit: "200" });
      if (filter !== "all") p.set("status", filter);
      const res = await api.get(`/admin/financial/invoices?${p}`);
      setInvoices(res.data.invoices || []);
      setSummary(res.data.summary || {});
    } catch { toast.error("Failed to load invoices"); }
    setLoading(false);
  }, [filter, sortBy, sortDir]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleStatus = async (id, status) => {
    try { await api.put(`/admin/financial/invoices/${id}`, { status }); toast.success(`Marked ${status}`); fetch_(); } catch { toast.error("Failed"); }
  };
  const handleGenerate = async () => {
    try { const r = await api.post("/admin/financial/invoices/generate"); toast.success(r.data.message); fetch_(); } catch { toast.error("Failed"); }
  };
  const handleSeed = async () => {
    try { const r = await api.post("/admin/financial/seed-invoices"); toast.success(r.data.message); fetch_(); } catch { toast.error("Failed"); }
  };

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return inv.tenant_name?.toLowerCase().includes(q) || inv.id?.toLowerCase().includes(q);
  });

  const toggleSort = (col) => { setSortBy(col); setSortDir(d => d === "asc" ? "desc" : "asc"); };
  const SortIcon = ({ col }) => sortBy === col ? (sortDir === "asc" ? <ArrowUp className="w-2.5 h-2.5 inline ml-0.5" /> : <ArrowDown className="w-2.5 h-2.5 inline ml-0.5" />) : null;
  const SC = { paid: "bg-green-50 text-green-700 border-green-200", pending: "bg-amber-50 text-amber-700 border-amber-200", overdue: "bg-red-50 text-red-700 border-red-200" };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard label="Outstanding" value={fmt(summary.total_outstanding)} icon={Clock} color="text-amber-600 bg-amber-50" />
        <KPICard label="Overdue" value={fmt(summary.total_overdue)} icon={AlertTriangle} color="text-red-600 bg-red-50" sub={`${summary.overdue_count || 0} invoices`} />
        <KPICard label="Collected" value={fmt(summary.total_paid)} icon={CheckCircle2} color="text-green-600 bg-green-50" />
        <KPICard label="Total Invoices" value={summary.total_invoices || 0} icon={Receipt} color="text-blue-600 bg-blue-50" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenant or invoice..." className="pl-8 h-8 text-sm" data-testid="invoice-search" />
        </div>
        {["all", "pending", "overdue", "paid"].map(f => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className="h-7 text-[11px] capitalize" onClick={() => setFilter(f)} data-testid={`filter-${f}`}>{f}</Button>
        ))}
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={handleSeed}><Plus className="w-3 h-3" /> Seed Demo</Button>
          <Button size="sm" className="h-7 text-[11px] gap-1" onClick={handleGenerate} data-testid="generate-invoices-btn"><RefreshCw className="w-3 h-3" /> Generate Monthly</Button>
        </div>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-8" /> : (
        <Card className="border shadow-none overflow-hidden" data-testid="invoices-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b bg-muted/30">
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">Invoice</th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left cursor-pointer hover:text-foreground" onClick={() => toggleSort("tenant_name")}>Tenant<SortIcon col="tenant_name" /></th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-center">Period</th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-center cursor-pointer hover:text-foreground" onClick={() => toggleSort("due_date")}>Due Date<SortIcon col="due_date" /></th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-right cursor-pointer hover:text-foreground" onClick={() => toggleSort("amount")}>Amount<SortIcon col="amount" /></th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-center">Users</th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-center cursor-pointer hover:text-foreground" onClick={() => toggleSort("status")}>Status<SortIcon col="status" /></th>
                <th className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-center">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(inv => (
                  <tr key={inv.id} className="border-b hover:bg-muted/20" data-testid={`invoice-${inv.id}`}>
                    <td className="p-2"><span className="text-xs font-mono font-medium">{inv.id}</span></td>
                    <td className="p-2"><span className="text-sm font-medium">{inv.tenant_name}</span></td>
                    <td className="p-2 text-center text-xs">{inv.period_label}</td>
                    <td className="p-2 text-center text-xs">{inv.due_date?.slice(0, 10)}</td>
                    <td className="p-2 text-right text-sm font-bold">${inv.amount?.toLocaleString()}</td>
                    <td className="p-2 text-center text-xs">{inv.billable_users}</td>
                    <td className="p-2 text-center">
                      <Badge className={cn("text-[9px] border capitalize", SC[inv.status] || "bg-gray-50")}>{inv.status}</Badge>
                      {inv.days_overdue > 0 && <p className="text-[9px] text-red-500">{inv.days_overdue}d</p>}
                    </td>
                    <td className="p-2 text-center">
                      {inv.status !== "paid" && <Button size="sm" variant="ghost" className="h-6 text-[10px] text-green-600" onClick={() => handleStatus(inv.id, "paid")}><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Button>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">No invoices found.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════════════════════════
   FORECASTS TAB
   ═══════════════════════════════════ */
const ForecastsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/financial/forecasts"); setData(r.data); } catch { toast.error("Failed to load forecasts"); }
      setLoading(false);
    })();
  }, []);

  const fetchAI = async () => {
    if (!data) return;
    setAiLoading(true);
    try {
      const r = await api.post("/admin/financial/ai-insights", { financial_data: data, focus_area: "forecasts_and_growth" });
      setAiInsight(r.data.insight);
    } catch { toast.error("AI insight failed"); }
    setAiLoading(false);
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-16" />;
  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">12-month revenue projections with scenario analysis</p>
        <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={fetchAI} disabled={aiLoading} data-testid="ai-forecast-btn">
          {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Insights
        </Button>
      </div>

      {aiInsight && (
        <Card className="border-2 border-violet-200 bg-violet-50/30 shadow-none" data-testid="ai-insight-card">
          <CardContent className="p-4"><div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /><div className="text-sm whitespace-pre-line">{aiInsight}</div></div></CardContent>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KPICard label="Current MRR" value={fmt(data.current.mrr)} icon={DollarSign} color="text-green-600 bg-green-50" />
        <KPICard label="Current ARR" value={fmt(data.current.arr)} icon={TrendingUp} color="text-blue-600 bg-blue-50" />
        <KPICard label="Active Users" value={data.current.total_users} icon={Users} color="text-violet-600 bg-violet-50" sub={`${data.current.total_billable} billable`} />
        <KPICard label="Tenants" value={data.current.tenants} icon={Building2} color="text-amber-600 bg-amber-50" />
      </div>

      {/* Scenario Line Chart */}
      <Card className="border shadow-none" data-testid="scenario-chart">
        <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">12-Month Scenario Curves</CardTitle></CardHeader>
        <CardContent className="px-2 pb-3">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.scenario_curves}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmt(v)} width={55} />
              <Tooltip formatter={v => fmt(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="Optimistic" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="Base" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="Conservative" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(data.scenarios).map(([key, s]) => (
          <div key={key} className={cn("p-4 rounded-lg border-2", key === "optimistic" ? "border-green-200 bg-green-50/30" : key === "base" ? "border-blue-200 bg-blue-50/30" : "border-amber-200 bg-amber-50/30")}>
            <p className="text-xs font-bold uppercase tracking-wider mb-2">{s.label}</p>
            <p className="text-lg font-bold">{fmt(s.mrr_12m)}<span className="text-xs font-normal text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground">{fmt(s.arr_12m)}/yr</p>
            <div className="mt-2 space-y-0.5"><p className="text-[10px]">Growth: {s.growth}</p><p className="text-[10px]">Churn: {s.churn}</p></div>
          </div>
        ))}
      </div>

      {/* MRR Projection Bar */}
      <Card className="border shadow-none" data-testid="mrr-projection-chart">
        <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Projected MRR (Base Case)</CardTitle></CardHeader>
        <CardContent className="px-2 pb-3">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.projections}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 9 }} tickFormatter={v => v?.split(" ")[0]} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmt(v)} width={55} />
              <Tooltip formatter={v => fmt(v)} />
              <Bar dataKey="projected_mrr" fill="#3b82f6" radius={[3, 3, 0, 0]} barSize={28} name="MRR" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Per-Tenant Projections */}
      <Card className="border shadow-none" data-testid="tenant-projections">
        <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Projected Revenue by Tenant</CardTitle></CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b bg-muted/30">
                {["Tenant", "Users", "Current MRR", "6-Mo MRR", "12-Mo MRR", "Growth", "Risk"].map(h => (
                  <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.tenant_projections?.map(t => (
                  <tr key={t.tenant_id} className="border-b hover:bg-muted/20">
                    <td className="p-2 text-sm font-medium">{t.tenant_name}</td>
                    <td className="p-2 text-sm">{t.users}</td>
                    <td className="p-2 text-sm font-bold">{fmt(t.current_mrr)}</td>
                    <td className="p-2 text-sm">{fmt(t.projected_mrr_6m)}</td>
                    <td className="p-2 text-sm">{fmt(t.projected_mrr_12m)}</td>
                    <td className="p-2 text-xs text-green-600">{t.growth_rate}%</td>
                    <td className="p-2"><Badge className={cn("text-[9px]", t.risk === "high" ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200")}>{t.risk}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Risk Factors */}
      {data.risk_factors?.length > 0 && (
        <Card className="border shadow-none border-red-100" data-testid="risk-factors">
          <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Risk Factors</CardTitle></CardHeader>
          <CardContent className="px-5 pb-4 space-y-2">
            {data.risk_factors.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg border border-red-100 bg-red-50/30">
                <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200 shrink-0">{r.severity}</Badge>
                <div><p className="text-sm font-medium">{r.tenant}</p><p className="text-xs text-muted-foreground">{r.detail}</p></div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ═══════════════════════════════════
   SUBSCRIPTION PLANS TAB
   ═══════════════════════════════════ */
const SubscriptionTab = () => {
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form, setForm] = useState({ name: "", price_per_user_monthly: 10, price_per_user_annual: 8, min_users: 50, max_users: null, features: "", sort_order: 99 });
  const [configEditing, setConfigEditing] = useState(false);
  const [configForm, setConfigForm] = useState({});

  const fetchAll = useCallback(async () => {
    try {
      const [plansRes, configRes] = await Promise.all([
        api.get("/admin/financial/plans"),
        api.get("/admin/financial/subscription-config"),
      ]);
      setPlans(plansRes.data || []);
      setConfig(configRes.data || {});
      setConfigForm(configRes.data || {});
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditPlan(null);
    setForm({ name: "", price_per_user_monthly: 10, price_per_user_annual: 8, min_users: 50, max_users: null, features: "", sort_order: 99 });
    setDialog(true);
  };
  const openEdit = (p) => {
    setEditPlan(p);
    setForm({ ...p, features: (p.features || []).join(", ") });
    setDialog(true);
  };
  const handleSave = async () => {
    const payload = { ...form, features: form.features?.split(",").map(f => f.trim()).filter(Boolean) || [] };
    try {
      if (editPlan) { await api.put(`/admin/financial/plans/${editPlan.id}`, payload); toast.success("Plan updated"); }
      else { await api.post("/admin/financial/plans", payload); toast.success("Plan created"); }
      setDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try { await api.delete(`/admin/financial/plans/${id}`); toast.success("Plan deleted"); fetchAll(); } catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };
  const handleConfigSave = async () => {
    try {
      await api.put("/admin/financial/subscription-config", configForm);
      setConfig(configForm);
      setConfigEditing(false);
      toast.success("Config saved");
    } catch { toast.error("Failed"); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-16" />;

  return (
    <div className="space-y-5">
      {/* Plans */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manage subscription tiers, pricing & free trial</p>
        <Button size="sm" className="h-7 text-[11px] gap-1" onClick={openCreate} data-testid="create-plan-btn"><Plus className="w-3 h-3" /> New Plan</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(p => (
          <Card key={p.id} className="border-2 shadow-none relative" data-testid={`plan-${p.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <Badge variant="outline" className="text-[9px] mb-1.5">{p.is_default ? "Default" : "Custom"}</Badge>
                  <h3 className="text-lg font-bold">{p.name}</h3>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEdit(p)}><Settings2 className="w-3 h-3" /></Button>
                  {!p.is_default && <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDelete(p.id)}><Trash2 className="w-3 h-3" /></Button>}
                </div>
              </div>
              <p className="text-2xl font-bold">${p.price_per_user_monthly}<span className="text-sm font-normal text-muted-foreground">/user/mo</span></p>
              <p className="text-xs text-muted-foreground">${p.price_per_user_annual}/user/mo annual</p>
              <p className="text-[10px] text-muted-foreground mt-1">Min {p.min_users} users{p.max_users ? ` / Max ${p.max_users}` : " / Unlimited"}</p>
              <p className="text-[10px] text-muted-foreground">Min rev: ${p.min_users * p.price_per_user_monthly}/mo</p>
              <div className="mt-3 space-y-1">
                {(p.features || []).slice(0, 5).map(f => (
                  <div key={f} className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-green-600 shrink-0" /><span className="text-[10px]">{f}</span></div>
                ))}
                {(p.features || []).length > 5 && <p className="text-[9px] text-muted-foreground">+{p.features.length - 5} more</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Global Config: Free Trial + Billing Rules */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border shadow-none" data-testid="free-trial-config">
          <CardHeader className="pt-4 pb-2 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4" /> Free Trial Settings</CardTitle>
              <Button size="sm" variant={configEditing ? "default" : "outline"} className="h-7 text-[11px]" onClick={() => configEditing ? handleConfigSave() : setConfigEditing(true)} data-testid="edit-config-btn">
                {configEditing ? "Save" : "Edit"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Free Trial Enabled</Label>
              <button className={cn("w-10 h-5 rounded-full transition-colors relative", configForm.free_trial_enabled ? "bg-green-500" : "bg-gray-300")}
                onClick={() => configEditing && setConfigForm(p => ({ ...p, free_trial_enabled: !p.free_trial_enabled }))} disabled={!configEditing} data-testid="free-trial-toggle">
                <span className={cn("w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform", configForm.free_trial_enabled ? "translate-x-5" : "translate-x-0.5")} />
              </button>
            </div>
            <div>
              <Label className="text-xs">Trial Duration (days)</Label>
              <Input type="number" value={configForm.free_trial_days || 14} disabled={!configEditing}
                onChange={e => setConfigForm(p => ({ ...p, free_trial_days: parseInt(e.target.value) }))} className="h-8 text-sm" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-none">
          <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Billing Rules</CardTitle></CardHeader>
          <CardContent className="px-5 pb-5 space-y-3">
            <div>
              <Label className="text-xs">Overdue Grace (days)</Label>
              <Input type="number" value={configForm.overdue_grace_days || 15} disabled={!configEditing}
                onChange={e => setConfigForm(p => ({ ...p, overdue_grace_days: parseInt(e.target.value) }))} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Late Fee (%)</Label>
              <Input type="number" step="0.1" value={configForm.late_fee_percent || 1.5} disabled={!configEditing}
                onChange={e => setConfigForm(p => ({ ...p, late_fee_percent: parseFloat(e.target.value) }))} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Auto-suspend after (days overdue)</Label>
              <Input type="number" value={configForm.auto_suspend_after_days || 45} disabled={!configEditing}
                onChange={e => setConfigForm(p => ({ ...p, auto_suspend_after_days: parseInt(e.target.value) }))} className="h-8 text-sm" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plan Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editPlan ? "Edit Plan" : "Create Plan"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Plan Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-8 text-sm" data-testid="plan-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Monthly ($/user)</Label><Input type="number" value={form.price_per_user_monthly} onChange={e => setForm(p => ({ ...p, price_per_user_monthly: parseFloat(e.target.value) }))} className="h-8 text-sm" /></div>
              <div><Label className="text-xs">Annual ($/user/mo)</Label><Input type="number" value={form.price_per_user_annual} onChange={e => setForm(p => ({ ...p, price_per_user_annual: parseFloat(e.target.value) }))} className="h-8 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Min Users</Label><Input type="number" value={form.min_users} onChange={e => setForm(p => ({ ...p, min_users: parseInt(e.target.value) }))} className="h-8 text-sm" /></div>
              <div><Label className="text-xs">Max Users (blank=unlimited)</Label><Input type="number" value={form.max_users || ""} onChange={e => setForm(p => ({ ...p, max_users: e.target.value ? parseInt(e.target.value) : null }))} className="h-8 text-sm" /></div>
            </div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) }))} className="h-8 text-sm" /></div>
            <div><Label className="text-xs">Features (comma-separated)</Label><Input value={form.features} onChange={e => setForm(p => ({ ...p, features: e.target.value }))} placeholder="Feature 1, Feature 2..." className="h-8 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} data-testid="save-plan-btn"><CheckCircle2 className="w-3 h-3 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
const AdminSettingsPage = () => {
  const [activeTab, setActiveTab] = useState("revenue");
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("this_quarter");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const r = await api.get(`/admin/financial/revenue-overview?period=${period}`); setRevenueData(r.data); } catch {}
      setLoading(false);
    })();
  }, [period]);

  return (
    <AdminLayout title="Revenue & Accounts">
      <div className="space-y-5 max-w-[1400px]" data-testid="admin-settings-page">
        <div>
          <h1 className="text-xl font-bold" data-testid="admin-settings-title">Revenue & Accounts</h1>
          <p className="text-sm text-muted-foreground">Enterprise financial management, billing, and projections</p>
        </div>

        <div className="flex items-center gap-1 border-b overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex items-center gap-1.5",
                activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`settings-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === "revenue" && <RevenueTab data={revenueData} loading={loading} period={period} setPeriod={setPeriod} />}
        {activeTab === "invoices" && <InvoicesTab />}
        {activeTab === "forecasts" && <ForecastsTab />}
        {activeTab === "subscription" && <SubscriptionTab />}
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
